import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { 
  Users, UserPlus, Key, Shield, Loader2, Trash2, Phone, Power, PowerOff,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const UsersManagement = () => {
  const { language } = useLanguage();
  const { token } = useAuth();
  
  const [allUsers, setAllUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userFormData, setUserFormData] = useState({
    name: '',
    phone: '',
    password: '',
    role: 'agent',
    employee_code: ''
  });
  const [submittingUser, setSubmittingUser] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const fetchAllUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.get(`${API_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAllUsers();
  }, [fetchAllUsers]);

  const handleCreateUser = async () => {
    if (!userFormData.name || !userFormData.phone || !userFormData.password) {
      toast.error(language === 'fr' ? 'Tous les champs sont requis' : 'All fields are required');
      return;
    }
    
    setSubmittingUser(true);
    try {
      await axios.post(`${API_URL}/users/create-with-role`, userFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'fr' ? 'Utilisateur créé' : 'User created');
      setUserDialogOpen(false);
      setUserFormData({ name: '', phone: '', password: '', role: 'agent', employee_code: '' });
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creating user');
    } finally {
      setSubmittingUser(false);
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await axios.put(`${API_URL}/users/${userId}/role`, { role: newRole }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'fr' ? 'Rôle mis à jour' : 'Role updated');
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating role');
    }
  };

  const handleToggleActive = async (user) => {
    const willDisable = user.is_active !== false;
    if (willDisable) {
      const ok = window.confirm(
        `Désactiver le compte de ${user.name} ?\n\n` +
        `Il ne pourra plus se connecter tant qu'il est désactivé. Cette action est réversible.`
      );
      if (!ok) return;
    }
    const reason = willDisable ? (window.prompt('Motif de désactivation (optionnel) :') || null) : null;
    try {
      await axios.post(
        `${API_URL}/users/${user.id}/toggle-active`,
        { is_active: !willDisable, reason },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(willDisable ? 'Compte désactivé' : 'Compte réactivé');
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleUpdateUserPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error(language === 'fr' ? 'Le mot de passe doit faire au moins 6 caractères' : 'Password must be at least 6 characters');
      return;
    }
    
    setSubmittingUser(true);
    try {
      await axios.put(`${API_URL}/users/${editingUser.id}/password`, { new_password: newPassword }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'fr' ? 'Mot de passe mis à jour' : 'Password updated');
      setPasswordDialogOpen(false);
      setNewPassword('');
      setEditingUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating password');
    } finally {
      setSubmittingUser(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      await axios.delete(`${API_URL}/users/${userToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'fr' ? 'Utilisateur supprimé' : 'User deleted');
      setDeleteUserDialogOpen(false);
      setUserToDelete(null);
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting user');
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'supervisor': return 'bg-purple-100 text-purple-800';
      case 'manager': return 'bg-blue-100 text-blue-800';
      case 'agent': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleSetEmail = async (userObj) => {
    const current = userObj.email || '';
    const value = window.prompt(
      `Email de ${userObj.name} (utilisé pour l'envoi automatique des bilans mensuels) :\n\nLaissez vide pour supprimer.`,
      current,
    );
    if (value === null) return; // cancel
    try {
      await axios.put(
        `${API_URL}/users/${userObj.id}/email`,
        { email: value.trim() },
        { headers: { Authorization: `Bearer ${localStorage.getItem('dermascan_token')}` } },
      );
      toast.success(value.trim() ? 'Email enregistré' : 'Email supprimé');
      fetchAllUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur enregistrement email');
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: language === 'fr' ? 'Administrateur' : 'Administrator',
      supervisor: language === 'fr' ? 'Superviseur' : 'Supervisor',
      manager: language === 'fr' ? 'Manager' : 'Manager',
      agent: language === 'fr' ? 'Agent' : 'Agent',
      client: language === 'fr' ? 'Client' : 'Client'
    };
    return labels[role] || role;
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#C9A086]" />
              {language === 'fr' ? 'Gestion des utilisateurs' : 'User Management'}
            </CardTitle>
            <Button 
              onClick={() => setUserDialogOpen(true)}
              className="bg-[#C9A086] hover:bg-[#B8907A]"
              data-testid="add-user-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Nouvel utilisateur' : 'New User'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingUsers ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-[#C9A086]" />
            </div>
          ) : (
            <div className="space-y-4">
              {allUsers.filter(u => u.role !== 'client').map((user) => (
                <div 
                  key={user.id} 
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                  data-testid={`user-row-${user.id}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#C9A086]/20 rounded-full flex items-center justify-center">
                        <Shield className="w-5 h-5 text-[#C9A086]" />
                      </div>
                      <div>
                        <p className="font-medium text-[#5D4037] flex items-center gap-2">
                          {user.name}
                          {user.is_active === false && (
                            <Badge variant="outline" className="border-red-300 text-red-700 bg-red-50 text-[10px]">
                              Désactivé
                            </Badge>
                          )}
                        </p>
                        <p className="text-sm text-[#78716C] flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {user.phone}
                        </p>
                        <button
                          onClick={() => handleSetEmail(user)}
                          className="text-[11px] text-emerald-700 hover:text-emerald-900 hover:underline mt-0.5 inline-flex items-center gap-1"
                          data-testid={`edit-email-${user.id}`}
                          title="Email pour bilans mensuels"
                        >
                          ✉ {user.email || <span className="italic text-zinc-400">aucun email · cliquer pour ajouter</span>}
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <Select 
                      value={user.role} 
                      onValueChange={(val) => handleUpdateUserRole(user.id, val)}
                      disabled={user.is_admin && allUsers.filter(u => u.is_admin).length <= 1}
                    >
                      <SelectTrigger className="w-40">
                        <Badge className={getRoleBadgeColor(user.role)}>
                          {getRoleLabel(user.role)}
                        </Badge>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="agent">{getRoleLabel('agent')}</SelectItem>
                        <SelectItem value="supervisor">{getRoleLabel('supervisor')}</SelectItem>
                        <SelectItem value="manager">{getRoleLabel('manager')}</SelectItem>
                        <SelectItem value="admin">{getRoleLabel('admin')}</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => { setEditingUser(user); setPasswordDialogOpen(true); }}
                    >
                      <Key className="w-4 h-4 mr-1" />
                      {language === 'fr' ? 'Mot de passe' : 'Password'}
                    </Button>

                    {!user.is_admin && (
                      <Button
                        variant="outline"
                        size="sm"
                        className={user.is_active === false
                          ? 'text-emerald-700 border-emerald-300 hover:bg-emerald-50'
                          : 'text-amber-700 border-amber-300 hover:bg-amber-50'}
                        onClick={() => handleToggleActive(user)}
                        title={user.is_active === false ? 'Réactiver' : 'Désactiver'}
                        data-testid={`toggle-active-${user.id}`}
                      >
                        {user.is_active === false ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
                      </Button>
                    )}
                    
                    {!user.is_admin && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-red-600 border-red-300 hover:bg-red-50"
                        onClick={() => { setUserToDelete(user); setDeleteUserDialogOpen(true); }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {language === 'fr' ? 'Créer un utilisateur' : 'Create User'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{language === 'fr' ? 'Nom complet' : 'Full name'}</Label>
              <Input 
                value={userFormData.name}
                onChange={(e) => setUserFormData({...userFormData, name: e.target.value})}
                placeholder="Ex: Jean Dupont"
                data-testid="user-name-input"
              />
            </div>
            <div>
              <Label>{language === 'fr' ? 'Téléphone' : 'Phone'}</Label>
              <Input 
                value={userFormData.phone}
                onChange={(e) => setUserFormData({...userFormData, phone: e.target.value})}
                placeholder="Ex: 0701234567"
                data-testid="user-phone-input"
              />
            </div>
            <div>
              <Label>{language === 'fr' ? 'Mot de passe' : 'Password'}</Label>
              <Input 
                type="password"
                value={userFormData.password}
                onChange={(e) => setUserFormData({...userFormData, password: e.target.value})}
                placeholder="Min 6 caractères"
                data-testid="user-password-input"
              />
            </div>
            <div>
              <Label>{language === 'fr' ? 'Rôle' : 'Role'}</Label>
              <Select 
                value={userFormData.role}
                onValueChange={(val) => setUserFormData({...userFormData, role: val})}
              >
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="agent">{getRoleLabel('agent')}</SelectItem>
                  <SelectItem value="supervisor">{getRoleLabel('supervisor')}</SelectItem>
                  <SelectItem value="manager">{getRoleLabel('manager')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{language === 'fr' ? 'Code employé (optionnel)' : 'Employee code (optional)'}</Label>
              <Input 
                value={userFormData.employee_code}
                onChange={(e) => setUserFormData({...userFormData, employee_code: e.target.value})}
                placeholder="Ex: EMP-001"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserDialogOpen(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleCreateUser} 
              disabled={submittingUser}
              className="bg-[#C9A086]"
              data-testid="create-user-submit"
            >
              {submittingUser && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {language === 'fr' ? 'Créer' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {language === 'fr' ? 'Changer le mot de passe' : 'Change Password'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#78716C]">
              {language === 'fr' 
                ? `Nouveau mot de passe pour ${editingUser?.name}`
                : `New password for ${editingUser?.name}`}
            </p>
            <div>
              <Label>{language === 'fr' ? 'Nouveau mot de passe' : 'New password'}</Label>
              <Input 
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Min 6 caractères"
                data-testid="new-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleUpdateUserPassword}
              disabled={submittingUser}
              className="bg-[#C9A086]"
              data-testid="update-password-submit"
            >
              {submittingUser && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {language === 'fr' ? 'Mettre à jour' : 'Update'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={deleteUserDialogOpen} onOpenChange={setDeleteUserDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{language === 'fr' ? 'Confirmer la suppression' : 'Confirm deletion'}</DialogTitle>
          </DialogHeader>
          <p className="text-[#78716C]">
            {language === 'fr' 
              ? `Voulez-vous vraiment supprimer l'utilisateur "${userToDelete?.name}" ?`
              : `Are you sure you want to delete user "${userToDelete?.name}"?`}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteUserDialogOpen(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button variant="destructive" onClick={handleDeleteUser}>
              {language === 'fr' ? 'Supprimer' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersManagement;
